import Header from "@/components/marketing/Header";
import Footer from "@/components/marketing/Footer";
import SEO from "@/components/marketing/SEO";
import FeaturesDetailed from "@/components/marketing/FeaturesDetailed";
import Guarantees from "@/components/marketing/Guarantees";
import BillingNote from "@/components/marketing/BillingNote";
import DemoRequestDialog from "@/components/marketing/DemoRequestDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CALENDLY_ONBOARDING_URL } from "@/config/support";
import { CalendarClock, Check, Scale, Sparkles } from "lucide-react";

// Date de fin de l'offre GloriaFood : à ajuster ici si Maxime la change.
const OFFER_DEADLINE = "28 février 2027";

const changes = [
  "Le bouton « Réserver une table » de ton site cesse de fonctionner.",
  "Tes clients ne peuvent plus réserver en ligne depuis ton site.",
  "Oracle ne propose aucun remplaçant direct à GloriaFood.",
];

const offer = [
  "29 CHF par mois, bloqué à vie tant que ton abonnement reste actif",
  "Mise en place offerte (au lieu de 100 CHF)",
  "60 jours gratuits, sans carte bancaire",
  "On remplace nous-mêmes le bouton GloriaFood sur ton site",
];

const steps = [
  {
    title: "Une démo de 20 minutes",
    text: "On te montre Piktable sur tes vrais horaires et tes vraies tables, en ligne ou chez toi.",
  },
  {
    title: "On configure ton restaurant",
    text: "Plan de salle, horaires, durée des tables, messages de confirmation : on prépare tout avec toi.",
  },
  {
    title: "On remplace le bouton",
    text: "Le bouton GloriaFood de ton site laisse la place à celui de Piktable. Si ton site est géré par une agence, on lui envoie le code et on s'en occupe avec elle.",
  },
];

const faq = [
  {
    q: "Quand est-ce que GloriaFood s'arrête ?",
    a: "Oracle a annoncé sur gloriafood.com que GloriaFood atteindra sa fin de vie le 30 avril 2027, sans remplaçant direct. Jusqu'à cette date, le service continue de fonctionner.",
  },
  {
    q: "Et la commande à emporter ?",
    a: "Piktable gère la réservation de table, pas la commande à emporter. Si tu utilises GloriaFood pour les deux, on te montre pendant la démo les solutions de commande qui fonctionnent à côté de Piktable.",
  },
  {
    q: "Quand faut-il changer ?",
    a: "Quand tu veux avant le 30 avril 2027. Plus tôt tu bascules, plus tes clients prennent l'habitude du nouveau bouton avant l'arrêt. L'offre GloriaFood est valable pour toute inscription jusqu'au " + OFFER_DEADLINE + ".",
  },
  {
    q: "Mon site est géré par une agence web. Comment ça se passe ?",
    a: "On fournit le code du bouton à ton agence, ou on fait le changement avec elle. Tu n'as rien de technique à faire.",
  },
  {
    q: "Combien ça coûte vraiment ?",
    a: "29 CHF par mois avec l'offre GloriaFood, bloqué à vie, sans commission par couvert. La mise en place est offerte et les 60 premiers jours sont gratuits.",
  },
  {
    q: "GloriaFood était gratuit. Pourquoi payer Piktable ?",
    a: "GloriaFood te donnait un bouton de réservation. Piktable gère toute ta salle : plan de tables, confirmations et rappels automatiques, suivi des absences, statistiques, sans commission par couvert. Et si tu trouves moins cher, aussi bien que Piktable, dis-le-nous : on s'aligne. C'est notre garantie Meilleur Prix.",
  },
];

const AlternativeGloriaFood = () => {
  const calendlyButton = (
    <Button asChild size="lg" variant="outline" className="text-lg px-8 bg-white/10 text-white border-white/40 hover:bg-white/20">
      <a href={CALENDLY_ONBOARDING_URL} target="_blank" rel="noopener noreferrer">
        Réserver une démo de 20 minutes
      </a>
    </Button>
  );

  return (
    <div className="min-h-screen">
      <SEO
        title="Alternative à GloriaFood en Suisse : Piktable"
        description="GloriaFood s'arrête le 30 avril 2027. Remplace ton bouton de réservation par Piktable : plan de salle, confirmations et rappels automatiques. 29 CHF par mois à vie pour les restaurants GloriaFood."
        canonical="/alternative-gloriafood"
      />
      <Header />
      <main className="pt-16">
        <section className="relative py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-accent opacity-95" />
          <div className="container mx-auto px-4 relative z-10 text-center">
            <div className="inline-flex items-center gap-2 bg-white/15 border border-white/30 rounded-full px-4 py-1.5 mb-6">
              <CalendarClock className="w-4 h-4 text-white" />
              <span className="text-xs uppercase tracking-widest text-white font-semibold">
                GloriaFood s'arrête le 30 avril 2027
              </span>
            </div>
            <h1 className="font-playfair text-4xl md:text-6xl font-bold text-white mb-6 max-w-3xl mx-auto leading-tight">
              Ton bouton GloriaFood s'arrête.<br />Pas tes réservations.
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed mb-10">
              Oracle arrête GloriaFood sans remplaçant. Piktable reprend ta réservation en ligne, et on s'occupe de l'installation sur ton site.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <DemoRequestDialog
                trigger={
                  <Button size="lg" variant="secondary" className="text-lg px-8">
                    Profiter de l'offre GloriaFood
                  </Button>
                }
                selectedPlan="Founders"
                context="cta"
              />
              {calendlyButton}
            </div>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-border bg-card">
                <CardContent className="p-8 space-y-4">
                  <h2 className="font-playfair text-2xl font-semibold text-foreground">
                    Ce qui change le 30 avril 2027
                  </h2>
                  <ul className="space-y-3">
                    {changes.map((c) => (
                      <li key={c} className="flex items-start gap-3">
                        <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2.5" />
                        <span className="text-foreground leading-relaxed">{c}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-muted-foreground">
                    Source : annonce d'Oracle sur{" "}
                    <a href="https://www.gloriafood.com/" target="_blank" rel="noopener noreferrer" className="underline">
                      gloriafood.com
                    </a>
                    .
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-primary bg-card shadow-lg">
                <CardContent className="p-8 space-y-6 relative">
                  <Badge className="absolute -top-3 left-6 bg-primary text-primary-foreground">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Jusqu'au {OFFER_DEADLINE}
                  </Badge>
                  <p className="text-xs uppercase tracking-widest text-primary font-semibold">
                    Offre GloriaFood
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="font-playfair text-6xl font-bold text-primary">29</span>
                    <span className="text-muted-foreground text-lg">CHF / mois · à vie</span>
                  </div>
                  <ul className="space-y-3">
                    {offer.map((o) => (
                      <li key={o} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-4 h-4 text-primary" />
                        </div>
                        <span className="text-foreground leading-relaxed">{o}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-start gap-3 rounded-lg bg-primary/5 border border-primary/20 p-4">
                    <Scale className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="font-semibold text-foreground">
                        Tu trouves moins cher, et aussi bien ? Dis-le-nous.
                      </p>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        On s'aligne : c'est notre garantie Meilleur Prix, à fonctionnalités équivalentes et sans commission.{" "}
                        <a href="mailto:contact@piktable.ch?subject=Garantie%20Meilleur%20Prix" className="underline">
                          Envoie-nous l'offre
                        </a>
                        .
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <FeaturesDetailed
          heading="Plus qu'un bouton : un vrai système de réservation"
          subheading="Plan de salle, confirmations et rappels automatiques, gestion des absences. Toutes les fonctionnalités incluses dès le premier jour."
        />

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-foreground mb-12 text-center">
              Comment ça se passe
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {steps.map((s, i) => (
                <Card key={s.title} className="border-border bg-card">
                  <CardContent className="p-6 space-y-3">
                    <span className="font-playfair text-4xl font-bold text-primary">{i + 1}</span>
                    <h3 className="font-semibold text-lg text-foreground">{s.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{s.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <BillingNote />

        <section className="py-20 bg-background">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="font-playfair text-4xl font-bold text-foreground mb-8 text-center">
              Questions fréquentes
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {faq.map((f) => (
                <AccordionItem key={f.q} value={f.q}>
                  <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-r from-primary/10 to-primary/5">
          <div className="container mx-auto px-4 text-center max-w-2xl">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-foreground mb-4">
              Ne laisse pas tes réservations s'arrêter avec GloriaFood
            </h2>
            <p className="text-muted-foreground mb-8">
              Offre GloriaFood valable jusqu'au {OFFER_DEADLINE}. On s'occupe de tout, tu gardes tes clients.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <DemoRequestDialog
                trigger={
                  <Button size="lg" className="text-lg px-8">
                    Profiter de l'offre GloriaFood
                  </Button>
                }
                selectedPlan="Founders"
                context="cta"
              />
              <Button asChild size="lg" variant="outline" className="text-lg px-8">
                <a href={CALENDLY_ONBOARDING_URL} target="_blank" rel="noopener noreferrer">
                  Réserver une démo de 20 minutes
                </a>
              </Button>
            </div>
          </div>
        </section>

        <Guarantees compact />
      </main>
      <Footer />
    </div>
  );
};

export default AlternativeGloriaFood;
